'use client';

import { buildResultsHref, classifyQuery } from '@ria-client/advisor-search/query';
import { useAnalyticsPageView } from '@ria-client/segment/Analytics/AnalyticsPageView';
import { useStandardTrack } from '@ria-client/segment/hooks/useStandardTrack';
import { Button } from '@ria-client/tw-shadcn/components/button';
import { Input } from '@ria-client/tw-shadcn/components/input';
import { useRouter } from 'next/navigation';
import type { ChangeEvent, FormEvent } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

const PLACEHOLDER = 'Enter zip code or DMA name';
const ARIA_LABEL = 'Zip code or DMA name';
const MAX_LENGTH = 64;
const FORM_NAME = 'advisor_search';

export type ZipSearchFormProps = {
  initialQuery?: string;
  isLoading?: boolean;
  onSubmitted?: (query: string) => void;
};

function describeError(trimmed: string): string {
  if (trimmed.length === 0) return 'required';
  if (/^\d+$/.test(trimmed)) return 'Enter a valid 5-digit zip code';
  return 'Enter at least 3 characters';
}

export function ZipSearchForm({
  initialQuery = '',
  isLoading = false,
  onSubmitted,
}: ZipSearchFormProps) {
  const router = useRouter();
  const track = useStandardTrack();
  const { pageViewReady } = useAnalyticsPageView();
  const [query, setQuery] = useState(initialQuery);
  const [showError, setShowError] = useState(false);
  const errorId = useId();
  const formViewEventId = useMemo(() => crypto.randomUUID(), []);
  const startedEventIdRef = useRef<string | null>(null);
  const formViewFiredRef = useRef(false);

  const trimmed = query.trim();
  const kind = classifyQuery(trimmed);
  const isInvalid = kind === 'invalid';
  const errorText = showError && isInvalid ? describeError(trimmed) : null;

  useEffect(() => {
    if (!pageViewReady || formViewFiredRef.current) return;
    formViewFiredRef.current = true;
    void track('Form Action', {
      form_action: 'viewed',
      form_name: FORM_NAME,
      event_id: formViewEventId,
    });
  }, [formViewEventId, pageViewReady, track]);

  function ensureStarted() {
    if (!formViewFiredRef.current) return null;
    if (startedEventIdRef.current) return startedEventIdRef.current;

    const startedId = crypto.randomUUID();
    startedEventIdRef.current = startedId;
    void track('Form Action', {
      form_action: 'started',
      form_name: FORM_NAME,
      event_id: startedId,
      parent_event_id: formViewEventId,
    });
    return startedId;
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    ensureStarted();
    setQuery(event.target.value);
    if (showError) setShowError(false);
  }

  function handleBlur() {
    if (trimmed.length > 0 && isInvalid) setShowError(true);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isInvalid) {
      setShowError(true);
      return;
    }
    if (isLoading) return;

    const parentEventId = ensureStarted();
    onSubmitted?.(trimmed);
    if (parentEventId) {
      void track('Form Action', {
        form_action: 'submitted',
        form_name: FORM_NAME,
        event_id: crypto.randomUUID(),
        parent_event_id: parentEventId,
        additional_attributes: { q: trimmed, kind },
      });
    }
    router.push(buildResultsHref({ q: trimmed, page: 1 }));
  }

  return (
    <form className="flex w-full flex-col" role="search" noValidate onSubmit={handleSubmit}>
      <div className="flex w-full items-stretch rounded-[100px] bg-background p-1">
        <Input
          value={query}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isLoading}
          placeholder={PLACEHOLDER}
          aria-label={ARIA_LABEL}
          aria-invalid={errorText != null}
          aria-describedby={errorText != null ? errorId : undefined}
          inputMode="text"
          maxLength={MAX_LENGTH}
          className="h-14 rounded-[100px] border-0 text-base font-medium"
        />
        <Button
          type="submit"
          disabled={isLoading || isInvalid}
          fullWidth={false}
          className="h-14 w-[150px] rounded-[100px] p-0"
        >
          <svg aria-hidden="true" className="size-5 fill-current" viewBox="0 0 24 24">
            <path d="M9.5 3a6.5 6.5 0 0 0 0 13c1.61 0 3.09-.59 4.23-1.57L19.3 20 21 18.3l-5.57-5.57A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 0 0 9.5 3m0 2A4.5 4.5 0 1 1 5 9.5 4.5 4.5 0 0 1 9.5 5" />
          </svg>
          SEARCH
        </Button>
      </div>
      {errorText != null ? (
        <p id={errorId} role="alert" className="mt-1 ml-2 text-xs text-primary-foreground">
          {errorText}
        </p>
      ) : null}
    </form>
  );
}
